import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  engineerService,
  CuratedField,
  CuratedGroup,
  CuratedSchema,
} from '../../services/engineerService';
import SignaturePad from './SignaturePad';
import EngineerPdfTemplateWizard from './EngineerPdfTemplateWizard';

type FilledData = Record<string, any>;

/**
 * Schema-driven certificate fill wizard.
 *
 * Flow:
 *   1. /engineer/jobs/:jobId/certificates/new   → pick a template
 *   2. /engineer/jobs/:jobId/certificates/new/:templateId → fill wizard
 *
 * The wizard creates one screen per schema group. Repeatable groups
 * ("Appliance 1", "Appliance 2"…) become a child flow the engineer can
 * expand inline.
 */
const EngineerCertificateWizard: React.FC = () => {
  const { jobId, templateId } = useParams<{ jobId: string; templateId?: string }>();
  const navigate = useNavigate();

  if (!templateId) {
    return <TemplatePicker jobId={jobId!} onPick={(id) => navigate(`/engineer/jobs/${jobId}/certificates/new/${id}`)} />;
  }

  return <FillRunner jobId={jobId!} templateId={templateId} />;
};

// ------------------------------------------------------------------
// STEP 1 — TEMPLATE PICKER
// ------------------------------------------------------------------

const TemplatePicker: React.FC<{ jobId: string; onPick: (id: string) => void }> = ({ jobId, onPick }) => {
  const navigate = useNavigate();
  const autoPickedRef = useRef(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['engineer', 'cert-templates', jobId],
    queryFn: () => engineerService.listCertificateTemplates(jobId),
  });

  useEffect(() => {
    const templates = data?.templates || [];
    if (templates.length !== 1 || autoPickedRef.current) return;
    autoPickedRef.current = true;
    onPick(templates[0].id);
  }, [data, onPick]);

  if (isLoading) return <div className="px-4 py-6 text-sm text-slate-500">Loading templates…</div>;
  if (error) return <div className="px-4 py-6 text-sm text-red-600">Couldn't load templates.</div>;

  const templates = data?.templates || [];

  return (
    <div className="max-w-xl mx-auto px-4 py-4 space-y-4">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500">← Back</button>
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Complete certificate</h1>
        <p className="text-sm text-slate-500 mt-1">Pick the certificate you're filling out on site.</p>
      </div>

      {templates.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          No certificate templates are available yet. Ask your admin to add approved templates (curated Gas Safe forms or company PDF
          checklists).
        </div>
      )}

      <ul className="space-y-2">
        {templates.map((t) => (
          <li key={t.id}>
            <button
              onClick={() => onPick(t.id)}
              className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-blue-400 hover:bg-blue-50/40 transition"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-base font-semibold text-slate-900">{t.templateLabel}</div>
                  {'fillMode' in t && t.fillMode === 'pdf_wizard' ? (
                    t.pdfWizardSummary ? (
                      <div className="text-xs text-slate-500 mt-1">{t.pdfWizardSummary}</div>
                    ) : null
                  ) : (
                    'schema' in t &&
                    t.schema?.summary && <div className="text-xs text-slate-500 mt-1">{t.schema.summary}</div>
                  )}
                </div>
                {'fillMode' in t && t.fillMode === 'pdf_wizard' ? (
                  <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                    PDF form
                  </span>
                ) : t.curatedKey ? (
                  <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                    {t.curatedKey}
                  </span>
                ) : null}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ------------------------------------------------------------------
// STEP 2 — FILL WIZARD
// ------------------------------------------------------------------

const FillRunner: React.FC<{ jobId: string; templateId: string }> = ({ jobId, templateId }) => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['engineer', 'cert-template', templateId, jobId],
    queryFn: () => engineerService.getCertificateTemplate(templateId, jobId),
  });

  if (isLoading) return <div className="px-4 py-6 text-sm text-slate-500">Loading template…</div>;
  if (error || !data) return <div className="px-4 py-6 text-sm text-red-600">Couldn't load template.</div>;

  if (data.fillMode === 'pdf_wizard') {
    return (
      <EngineerPdfTemplateWizard
        jobId={jobId}
        templateId={templateId}
        templateMeta={{
          templateLabel: data.templateMeta.templateLabel,
          schemaJson: data.templateMeta.schemaJson,
        }}
        pdfWizard={data.pdfWizard}
        onCancel={() => navigate(-1)}
      />
    );
  }

  if (data.fillMode === 'curated') {
    return <WizardBody jobId={jobId} templateId={templateId} schema={data.schema} onCancel={() => navigate(-1)} />;
  }

  return (
    <div className="px-4 py-6 text-sm text-red-600">
      This template format isn&apos;t supported on the engineer app yet.
    </div>
  );
};

const WizardBody: React.FC<{
  jobId: string;
  templateId: string;
  schema: CuratedSchema;
  onCancel: () => void;
}> = ({ jobId, templateId, schema, onCancel }) => {
  const navigate = useNavigate();

  // Expand repeatable groups: each group contributes N step screens
  const steps = useMemo(() => buildSteps(schema), [schema]);
  const [stepIdx, setStepIdx] = useState(0);
  const [filled, setFilled] = useState<FilledData>(() => seedDefaults(schema));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<{ id: string; certificateNumber: string; signedUrl: string | null } | null>(null);

  const submitMut = useMutation({
    mutationFn: (payload: {
      jobId: string;
      templateId: string;
      schemaVersion: number;
      filledData: FilledData;
    }) => engineerService.submitCertificate(payload),
    onSuccess: (res) => setResult(res),
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || 'Submission failed';
      const details = err?.response?.data?.errors;
      setSubmitError(Array.isArray(details) ? details.join('\n') : String(msg));
    },
  });

  if (result) return <SuccessView result={result} onDone={() => navigate(`/engineer/jobs/${jobId}`)} />;

  const step = steps[stepIdx];
  const isReview = step.kind === 'review';
  const totalSteps = steps.length;
  const progress = Math.round(((stepIdx + 1) / totalSteps) * 100);

  const setValue = (fullKey: string, value: any) => {
    setFilled((prev) => ({ ...prev, [fullKey]: value }));
    if (errors[fullKey]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[fullKey];
        return next;
      });
    }
  };

  const validateStep = () => {
    if (step.kind === 'group') {
      const stepErrors: Record<string, string> = {};
      for (const section of step.sections) {
        for (const field of section.fields) {
          const fullKey = step.prefix + field.key;
          if (!isFieldVisible(field, filled, step.prefix)) continue;
          const value = filled[fullKey];
          const msg = validateField(field, value);
          if (msg) stepErrors[fullKey] = msg;
        }
      }
      if (Object.keys(stepErrors).length) {
        setErrors((prev) => ({ ...prev, ...stepErrors }));
        return false;
      }
    }
    return true;
  };

  const onNext = () => {
    if (!validateStep()) return;
    if (stepIdx < totalSteps - 1) {
      setStepIdx((i) => i + 1);
      window.scrollTo({ top: 0, behavior: 'instant' as any });
    }
  };

  const onBack = () => {
    if (stepIdx > 0) setStepIdx((i) => i - 1);
  };

  const onSubmit = async () => {
    setSubmitError(null);
    submitMut.mutate({
      jobId,
      templateId,
      schemaVersion: schema.version,
      filledData: filled,
    });
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-4 pb-28">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onCancel} className="text-sm text-slate-500">✕ Cancel</button>
        <div className="text-xs font-medium text-slate-500">
          Step {stepIdx + 1} of {totalSteps}
        </div>
      </div>
      <div className="w-full h-1.5 bg-slate-200 rounded-full mt-3 overflow-hidden">
        <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress}%` }} />
      </div>

      <h1 className="mt-4 text-xl font-semibold text-slate-900">
        {step.kind === 'review' ? 'Review & submit' : step.title}
      </h1>
      {step.kind === 'group' && step.description && (
        <p className="text-sm text-slate-500 mt-1">{step.description}</p>
      )}

      {/* Body */}
      <div className="mt-5 space-y-6">
        {step.kind === 'group' &&
          step.sections.map((section) => (
            <SectionView
              key={section.key}
              title={section.label}
              description={section.description}
            >
              {section.fields.map((field) => {
                const fullKey = step.prefix + field.key;
                if (!isFieldVisible(field, filled, step.prefix)) return null;
                return (
                  <FieldView
                    key={fullKey}
                    field={field}
                    value={filled[fullKey]}
                    error={errors[fullKey]}
                    onChange={(v) => setValue(fullKey, v)}
                  />
                );
              })}
            </SectionView>
          ))}

        {step.kind === 'review' && (
          <ReviewView schema={schema} filled={filled} />
        )}
      </div>

      {submitError && (
        <pre className="mt-4 text-xs whitespace-pre-wrap bg-red-50 border border-red-200 text-red-700 rounded-lg p-3">
          {submitError}
        </pre>
      )}

      {/* Footer nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 z-30">
        <div className="max-w-xl mx-auto flex gap-2">
          <button
            onClick={onBack}
            disabled={stepIdx === 0}
            className="flex-1 py-3 rounded-lg border border-slate-300 text-slate-700 font-semibold disabled:opacity-40"
          >
            Back
          </button>
          {isReview ? (
            <button
              onClick={onSubmit}
              disabled={submitMut.isPending}
              className="flex-1 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-60"
            >
              {submitMut.isPending ? 'Submitting…' : 'Submit certificate'}
            </button>
          ) : (
            <button
              onClick={onNext}
              className="flex-1 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              {stepIdx === totalSteps - 2 ? 'Review' : 'Next'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// STEP BUILDER
// ------------------------------------------------------------------

type GroupStep = {
  kind: 'group';
  title: string;
  description?: string;
  prefix: string; // '' for non-repeating, `${groupKey}[${i}].` for repeating
  sections: CuratedGroup['sections'];
};
type ReviewStep = { kind: 'review' };
type WizardStep = GroupStep | ReviewStep;

function buildSteps(schema: CuratedSchema): WizardStep[] {
  const steps: WizardStep[] = [];
  for (const group of schema.groups) {
    if (group.repeatable) {
      const count = Math.max(group.repeatable.min, 1);
      for (let i = 0; i < count; i += 1) {
        steps.push({
          kind: 'group',
          title: group.repeatable.labelTemplate.replace(/\{n\}/g, String(i + 1)),
          description: group.description,
          prefix: `${group.key}[${i}].`,
          sections: group.sections,
        });
      }
    } else {
      steps.push({
        kind: 'group',
        title: group.label,
        description: group.description,
        prefix: '',
        sections: group.sections,
      });
    }
  }
  steps.push({ kind: 'review' });
  return steps;
}

function seedDefaults(schema: CuratedSchema): FilledData {
  const out: FilledData = {};
  for (const group of schema.groups) {
    const prefixes = group.repeatable
      ? Array.from(
          { length: Math.max(group.repeatable.min, 1) },
          (_, i) => `${group.key}[${i}].`,
        )
      : [''];
    for (const prefix of prefixes) {
      for (const section of group.sections) {
        for (const field of section.fields) {
          if (field.default !== undefined && field.default !== null) {
            out[prefix + field.key] = field.default;
          }
        }
      }
    }
  }
  return out;
}

// ------------------------------------------------------------------
// FIELD RENDERING
// ------------------------------------------------------------------

function isFieldVisible(field: CuratedField, filled: FilledData, prefix: string): boolean {
  if (!field.visibleWhen) return true;
  const gateKey = prefix + field.visibleWhen.key;
  const gate = filled[gateKey];
  return field.visibleWhen.in.some((v) => v === gate);
}

function validateField(field: CuratedField, value: any): string | null {
  if (field.required && (value === null || value === undefined || value === '')) {
    return 'Required';
  }
  if (value === null || value === undefined || value === '') return null;
  const v = field.validation;
  if (v?.min !== undefined && typeof value === 'number' && value < v.min) return `Must be ≥ ${v.min}`;
  if (v?.max !== undefined && typeof value === 'number' && value > v.max) return `Must be ≤ ${v.max}`;
  if (v?.pattern && typeof value === 'string') {
    try {
      if (!new RegExp(v.pattern).test(value)) return 'Invalid format';
    } catch {}
  }
  if (v?.maxLength && typeof value === 'string' && value.length > v.maxLength) {
    return `Max ${v.maxLength} characters`;
  }
  return null;
}

const SectionView: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      {description && <div className="text-xs text-slate-500 mt-0.5">{description}</div>}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const FieldView: React.FC<{
  field: CuratedField;
  value: any;
  error?: string;
  onChange: (value: any) => void;
}> = ({ field, value, error, onChange }) => {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
        {field.validation?.unit && (
          <span className="ml-2 text-xs font-normal text-slate-400">({field.validation.unit})</span>
        )}
      </label>
      <FieldInput field={field} value={value} onChange={onChange} />
      {field.hint && <div className="mt-1 text-xs text-slate-500">{field.hint}</div>}
      {error && <div className="mt-1 text-xs text-red-600 font-medium">{error}</div>}
      <AdvisoryWarning field={field} value={value} />
    </div>
  );
};

const AdvisoryWarning: React.FC<{ field: CuratedField; value: any }> = ({ field, value }) => {
  const v = field.validation;
  if (!v || typeof value !== 'number') return null;
  if (v.advisoryMin !== undefined && value < v.advisoryMin) {
    return (
      <div className="mt-1 text-xs text-amber-700 font-medium">
        Below advisory range (≥ {v.advisoryMin}{v.unit ? ` ${v.unit}` : ''}) — please double-check.
      </div>
    );
  }
  if (v.advisoryMax !== undefined && value > v.advisoryMax) {
    return (
      <div className="mt-1 text-xs text-amber-700 font-medium">
        Above advisory range (≤ {v.advisoryMax}{v.unit ? ` ${v.unit}` : ''}) — please double-check.
      </div>
    );
  }
  return null;
};

const FieldInput: React.FC<{
  field: CuratedField;
  value: any;
  onChange: (v: any) => void;
}> = ({ field, value, onChange }) => {
  const common =
    'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none';

  switch (field.type) {
    case 'textarea':
    case 'address':
      return (
        <textarea
          className={common}
          rows={3}
          placeholder={field.placeholder}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'number':
    case 'integer':
      return (
        <input
          type="number"
          inputMode="decimal"
          step={field.type === 'integer' ? 1 : 'any'}
          className={common}
          placeholder={field.placeholder}
          value={value ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') onChange(null);
            else onChange(Number(v));
          }}
        />
      );

    case 'boolean':
      return (
        <div className="grid grid-cols-2 gap-2">
          <PillOption active={value === true} onClick={() => onChange(true)} label="Yes" />
          <PillOption active={value === false} onClick={() => onChange(false)} label="No" />
        </div>
      );

    case 'passfail':
      return (
        <div className="grid grid-cols-3 gap-2">
          <PillOption active={value === 'PASS'} onClick={() => onChange('PASS')} label="Pass" tone="green" />
          <PillOption active={value === 'FAIL'} onClick={() => onChange('FAIL')} label="Fail" tone="red" />
          <PillOption active={value === 'NA'} onClick={() => onChange('NA')} label="N/A" />
        </div>
      );

    case 'select':
      return (
        <select
          className={common}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="" disabled>Select…</option>
          {field.options?.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );

    case 'multiselect':
      return (
        <div className="grid grid-cols-2 gap-2">
          {field.options?.map((o) => {
            const arr: string[] = Array.isArray(value) ? value : [];
            const active = arr.includes(o.value);
            return (
              <PillOption
                key={o.value}
                active={active}
                onClick={() =>
                  onChange(active ? arr.filter((x) => x !== o.value) : [...arr, o.value])
                }
                label={o.label}
              />
            );
          })}
        </div>
      );

    case 'date':
      return (
        <input
          type="date"
          className={common}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'datetime':
      return (
        <input
          type="datetime-local"
          className={common}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'time':
      return (
        <input
          type="time"
          className={common}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'signature':
      return <SignatureField value={value} onChange={onChange} />;

    case 'photo':
      return <PhotoField value={value} onChange={onChange} />;

    default:
      return (
        <input
          type="text"
          className={common}
          placeholder={field.placeholder}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

const PillOption: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  tone?: 'default' | 'green' | 'red';
}> = ({ label, active, onClick, tone = 'default' }) => {
  const activeStyles =
    tone === 'green'
      ? 'bg-emerald-600 border-emerald-600 text-white'
      : tone === 'red'
      ? 'bg-red-600 border-red-600 text-white'
      : 'bg-blue-600 border-blue-600 text-white';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border px-4 py-2.5 font-semibold text-sm transition ${
        active ? activeStyles : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
      }`}
    >
      {label}
    </button>
  );
};

// ------------------------------------------------------------------
// SIGNATURE & PHOTO FIELDS
// ------------------------------------------------------------------

const SignatureField: React.FC<{ value: any; onChange: (v: any) => void }> = ({ value, onChange }) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async (url: string | null) => {
    setDataUrl(url);
    if (!url) {
      onChange(null);
      return;
    }
    setUploading(true);
    setErr(null);
    try {
      const { signedUrl } = await engineerService.uploadCertAsset('signature', url, 'image/png');
      onChange(signedUrl);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Upload failed');
      onChange(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {value && !dataUrl ? (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 flex items-center gap-3">
          <img src={value} alt="Signature" className="h-14 bg-white rounded" />
          <div className="flex-1 text-xs text-slate-500">Saved</div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs font-semibold text-red-600"
          >
            Re-sign
          </button>
        </div>
      ) : (
        <>
          <SignaturePad value={dataUrl} onChange={save} />
          {uploading && <div className="text-xs text-slate-500 mt-1">Uploading…</div>}
          {err && <div className="text-xs text-red-600 mt-1">{err}</div>}
        </>
      )}
    </div>
  );
};

const PhotoField: React.FC<{ value: any; onChange: (v: any) => void }> = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onFile = async (f: File) => {
    setUploading(true);
    setErr(null);
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => resolve(String(reader.result));
        reader.readAsDataURL(f);
      });
      const { signedUrl } = await engineerService.uploadCertAsset('photo', dataUrl, f.type || 'image/jpeg');
      onChange(signedUrl);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {value ? (
        <div className="border border-slate-200 rounded-lg p-2 bg-slate-50 flex items-center gap-3">
          <img src={value} alt="Uploaded" className="h-20 w-20 object-cover rounded" />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs font-semibold text-red-600"
          >
            Remove
          </button>
        </div>
      ) : (
        <label className="flex items-center justify-center w-full border-2 border-dashed border-slate-300 rounded-lg p-6 text-sm text-slate-500 hover:border-blue-400 hover:bg-blue-50/40 cursor-pointer">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
            }}
          />
          {uploading ? 'Uploading…' : 'Tap to take photo / upload'}
        </label>
      )}
      {err && <div className="text-xs text-red-600 mt-1">{err}</div>}
    </div>
  );
};

// ------------------------------------------------------------------
// REVIEW STEP
// ------------------------------------------------------------------

const ReviewView: React.FC<{ schema: CuratedSchema; filled: FilledData }> = ({ schema, filled }) => {
  const renderValue = (field: CuratedField, value: any) => {
    if (value === null || value === undefined || value === '') return '—';
    if (field.type === 'signature' || field.type === 'photo') {
      return <img src={String(value)} alt={field.label} className="h-20 rounded border border-slate-200 bg-white" />;
    }
    if (field.type === 'boolean') return value ? 'Yes' : 'No';
    if (field.type === 'select') {
      return field.options?.find((o) => o.value === value)?.label || String(value);
    }
    if (field.type === 'multiselect' && Array.isArray(value)) {
      return value.map((v) => field.options?.find((o) => o.value === v)?.label || v).join(', ');
    }
    return String(value);
  };

  return (
    <div className="space-y-4">
      {schema.groups.map((group) => {
        const instances = group.repeatable
          ? Array.from({ length: Math.max(group.repeatable.min, 1) }, (_, i) => ({
              prefix: `${group.key}[${i}].`,
              label: group.repeatable!.labelTemplate.replace(/\{n\}/g, String(i + 1)),
            }))
          : [{ prefix: '', label: group.label }];

        return (
          <div key={group.key} className="space-y-2">
            {instances.map((inst) => (
              <div key={inst.prefix} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 mb-2">
                  {inst.label}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {group.sections.flatMap((s) =>
                    s.fields
                      .filter((f) => isFieldVisible(f, filled, inst.prefix))
                      .map((f) => (
                        <div key={inst.prefix + f.key}>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            {f.label}
                          </div>
                          <div className="text-sm text-slate-800 mt-0.5 break-words">
                            {renderValue(f, filled[inst.prefix + f.key])}
                          </div>
                        </div>
                      )),
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// ------------------------------------------------------------------
// SUCCESS VIEW
// ------------------------------------------------------------------

const SuccessView: React.FC<{
  result: { id: string; certificateNumber: string; signedUrl: string | null };
  onDone: () => void;
}> = ({ result, onDone }) => {
  return (
    <div className="max-w-xl mx-auto px-4 py-10 space-y-5">
      <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-3xl mx-auto">
        ✓
      </div>
      <div className="text-center">
        <h1 className="text-xl font-semibold text-slate-900">Certificate submitted</h1>
        <div className="text-sm text-slate-500 mt-1">Number {result.certificateNumber}</div>
      </div>

      {result.signedUrl && (
        <a
          href={result.signedUrl}
          target="_blank"
          rel="noreferrer"
          className="block text-center py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold"
        >
          View PDF
        </a>
      )}

      <button
        onClick={onDone}
        className="block w-full text-center py-3 rounded-lg border border-slate-300 text-slate-700 font-semibold"
      >
        Back to job
      </button>
    </div>
  );
};

export default EngineerCertificateWizard;
